/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Sortable list
 */

import React, { useMemo } from 'react';
import _ from 'lodash';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { CheckboxChangeEvent, List } from 'antd';
import { SortableContext, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import SortableItem from './SortableItem';
import { isSpecialColumn } from './utils';
import { MAX_CUSTOM_FIELD_COLUMNS } from 'app-constants/admin/customAttribute';

interface IProps {
	items: IColumnMeta[];
	listStyle?: React.CSSProperties;
	onChange: (dataSource: IColumnMeta[]) => void;
}

const SortableList: React.FC<IProps> = (props) => {
	const { items, onChange } = props;

	// Create array of IDs for SortableContext
	const itemIds = useMemo(() => _.map(items, (item) => item.colId), [items]);
	const visibleCustomFieldCount = useMemo(() => _.size(_.filter(items, (item) => item.isCustomField && !item.hide)), [items]);
	const isCustomFieldLimitReached = visibleCustomFieldCount >= MAX_CUSTOM_FIELD_COLUMNS;

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handlePinned = (colId: string, pinned: 'left' | boolean) => {
		onChange(
			_.map(items, (item) => {
				if (item.colId === colId) {
					let newItem = { ...item, pinned };
					// VER-403 enable the disabled item if pinned by user
					if (pinned) {
						newItem = { ...newItem, hide: false };
					}
					return newItem;
				}
				return item;
			}),
		);
	};

	const handleCheckboxChange = (event: CheckboxChangeEvent, colId: string) => {
		onChange(
			_.map(items, (item) => {
				if (item.colId === colId) {
					const hide = !event.target.checked;
					let newItem = { ...item, hide };
					// VER-403 unpin item if disabled by user
					if (hide) {
						newItem = _.omit(newItem, 'pinned');
					}
					return newItem;
				}
				return item;
			}),
		);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!isSpecialColumn(active.id as string) && !isSpecialColumn(over.id as string) && over && active.id !== over.id) {
			const oldIndex = items.findIndex((item) => item.colId === active.id);
			const newIndex = items.findIndex((item) => item.colId === over.id);

			if (oldIndex !== newIndex) {
				onChange(arrayMove(items, oldIndex, newIndex));
			}
		}
	};

	return (
		<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
			<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
				<List
					size='small'
					style={props.listStyle}
					dataSource={items}
					renderItem={(item) => (
						<SortableItem
							key={item.colId}
							value={item}
							isCustomFieldLimitReached={isCustomFieldLimitReached}
							onCheckboxChange={handleCheckboxChange}
							onPinnedChange={handlePinned}
						/>
					)}
				/>
			</SortableContext>
		</DndContext>
	);
};

export default SortableList;
